import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Dropdown from '../components/Dropdown';
import Icon from '../components/Icon';
import SchoolFormModal from '../components/SchoolFormModal';
import { ErrorBanner, Toast } from '../components/Feedback';
import { api } from '../api/client';
import { useAuth } from '../context/AuthContext';
import { isAdministration } from '../roles';

const EMPTY_PAGE = { items: [], page: 0, totalItems: 0, hasNext: false };

function StatusPill({ status, label }) {
  const tone = status === 'ACTIVE' ? 'badge-ok' : status === 'INACTIVE' ? 'badge-danger' : 'badge-warn';
  return <span className={`badge ${tone}`}>{label ?? status}</span>;
}

export default function SchoolsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [page, setPage] = useState(EMPTY_PAGE);
  const [filters, setFilters] = useState({ query: '', boardId: '', customerStatus: '', stateId: '' });
  const [options, setOptions] = useState(null);
  // Boards and states are short, closed lists, so the whole master is fetched once and
  // the filters are plain selects. Cities are not, which is why the form uses a picker.
  const [boards, setBoards] = useState([]);
  const [states, setStates] = useState([]);
  const [salesReps, setSalesReps] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);

  // The School Master is maintained centrally: a rep reads their states and works them,
  // but the record itself — its code, its board, its city — is the Super Admin's.
  const isAdmin = isAdministration(user?.role);

  useEffect(() => {
    api.clientConfig().then((config) => setOptions(config.schools)).catch(() => setOptions(null));
    api.lookupBoards().then(setBoards).catch(() => setBoards([]));
    api.lookupStates().then(setStates).catch(() => setStates([]));
    // Only a Super Admin can assign a rep, and only they can read the user list.
    if (isAdmin) {
      api
        .adminListUsers({ role: 'SALES_REP', status: 'ACTIVE', size: 100 })
        .then((result) => setSalesReps(result.items))
        .catch(() => setSalesReps([]));
    }
  }, [isAdmin]);

  const load = useCallback(
    async (pageNumber = 0) => {
      setLoading(true);
      try {
        setPage(await api.listSchools({ ...filters, page: pageNumber, size: 20 }));
      } catch (err) {
        setError(err);
      } finally {
        setLoading(false);
      }
    },
    [filters],
  );

  useEffect(() => {
    // Debounced so typing in the search box does not fire a request per keystroke.
    const handle = setTimeout(() => load(0), 250);
    return () => clearTimeout(handle);
  }, [load]);

  async function createSchool(payload) {
    const created = await api.createSchool(payload);
    setShowForm(false);
    setToast(`${created.name} created · ${created.code}`);
    await load(0);
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h1>{user?.role === 'SALES_REP' ? 'My Schools' : 'School Management'}</h1>
          <p>
            {user?.role === 'SALES_REP'
              ? 'The schools standing in the states assigned to you. The records themselves are maintained centrally.'
              : 'The school list used across registration, verification and licensing.'}
          </p>
        </div>
        {isAdmin && (
          <button type="button" className="btn btn-primary" onClick={() => setShowForm(true)}>
            <Icon name="plus" size={15} />
            Add school
          </button>
        )}
      </div>

      <ErrorBanner error={error} onDismiss={() => setError(null)} />

      <section className="card card-pad">
        <div className="toolbar">
          <input
            className="input"
            style={{ flex: '1 1 220px' }}
            placeholder="Search name, code or city"
            aria-label="Search schools"
            maxLength={100}
            value={filters.query}
            onChange={(event) => setFilters({ ...filters, query: event.target.value })}
          />
          <div style={{ width: 170 }}>
            <Dropdown
              id="boardFilter"
              options={boards}
              value={filters.boardId}
              onChange={(boardId) => setFilters({ ...filters, boardId })}
              placeholder="All boards"
              ariaLabel="Filter by board"
            />
          </div>
          <div style={{ width: 170 }}>
            <Dropdown
              id="customerStatusFilter"
              options={options?.customerStatuses ?? []}
              value={filters.customerStatus}
              onChange={(customerStatus) => setFilters({ ...filters, customerStatus })}
              placeholder="All statuses"
              ariaLabel="Filter by customer status"
            />
          </div>
          <div style={{ width: 190 }}>
            <Dropdown
              id="stateFilter"
              options={states}
              value={filters.stateId}
              onChange={(stateId) => setFilters({ ...filters, stateId })}
              placeholder="All states"
              ariaLabel="Filter by state"
            />
          </div>
        </div>
      </section>

      <section className="card">
        <div className="tbl-wrap">
          <table className="tbl">
            <thead>
              <tr>
                <th>School</th>
                <th>Code</th>
                <th>Location</th>
                <th>Board</th>
                <th>Status</th>
                <th>Sales rep</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {page.items.map((school) => (
                <tr key={school.id}>
                  <td>{school.name}</td>
                  <td className="mono">{school.code}</td>
                  <td>
                    {school.city?.name}
                    <span className="muted small"> · {school.state?.name}</span>
                  </td>
                  <td>{school.board?.name}</td>
                  <td>
                    <StatusPill status={school.customerStatus} label={school.customerStatusLabel} />
                  </td>
                  <td>{school.assignedSalesRep?.fullName ?? <span className="muted">Unassigned</span>}</td>
                  <td style={{ textAlign: 'right' }}>
                    <button
                      type="button"
                      className="btn btn-sm btn-secondary"
                      onClick={() => navigate(`/schools/${school.id}`)}
                    >
                      Open
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {loading && <div className="empty">Loading…</div>}
        {!loading && page.items.length === 0 && (
          <div className="empty">No schools match these filters.</div>
        )}

        <div className="toolbar" style={{ padding: 'var(--s1) var(--s2)', justifyContent: 'space-between' }}>
          <span className="muted small">{page.totalItems} school(s)</span>
          <span style={{ display: 'flex', gap: 'var(--s0)' }}>
            <button type="button" className="btn btn-secondary btn-sm" disabled={page.page === 0}
                    onClick={() => load(page.page - 1)}>
              Previous
            </button>
            <button type="button" className="btn btn-secondary btn-sm" disabled={!page.hasNext}
                    onClick={() => load(page.page + 1)}>
              Next
            </button>
          </span>
        </div>
      </section>

      {showForm && (
        <SchoolFormModal
          options={options}
          salesReps={salesReps}
          canAssignRep={isAdmin}
          onClose={() => setShowForm(false)}
          onSave={createSchool}
        />
      )}

      <Toast message={toast} onDone={() => setTimeout(() => setToast(null), 2600)} />
    </>
  );
}
